import React, { useCallback, useEffect, useState } from 'react'
import InputField from '../InputField/InputField'
import { ReactComponent as Search } from '../../assets/icons/searchicon.svg'
import { ReactComponent as CloseIcon } from '../../assets/icons/smallcloseicon.svg'
import './SearchBar.scss'
import { debounce } from '../../utils/helperFunctions/helperFunctions'
import DOMPurify from 'dompurify'

const SearchBar = ({
  label,
  placeholder,
  searchText,
  setSearchText,
  onSearchFieldChange,
  setDataSource = () => {},
  searchP,
  // Optional business-field validation, applied on top of the generic XSS
  // check below. Both are opt-in so every other existing SearchBar usage
  // keeps its current (XSS-only) behavior unless a caller opts in.
  allowedPattern,
  maxLength,
}) => {
  const [inputText, setInputText] = useState('')
  const [inputError, setInputError] = useState(null)

  const sanitizeAndValidateInput = (text) => {
    // Fixed: More secure and efficient XSS detection patterns
    const scriptPattern = /<script[^>]*>|<\/script>/gi
    const onEventPattern = /\bon\w+\s*=/gi
    const javascriptPattern = /javascript:/gi
    // Check for XSS patterns separately to avoid ReDoS
    if (
      scriptPattern.test(text) ||
      onEventPattern.test(text) ||
      javascriptPattern.test(text)
    ) {
      setInputError('Invalid input: Potential XSS attempt detected')
      return ''
    }

    const cleanText = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      FORBID_TAGS: ['style', 'script', 'iframe', 'frame', 'object', 'embed'],
      FORBID_ATTR: ['onclick', 'onerror', 'onload', 'onmouseover'],
    })

    // Simplified validation check
    if (cleanText !== text) {
      setInputError('Invalid characters detected')
      return ''
    }

    if (maxLength && cleanText.length > maxLength) {
      setInputError(`Maximum ${maxLength} characters allowed`)
      return ''
    }

    if (allowedPattern && cleanText !== '' && !allowedPattern.test(cleanText)) {
      setInputError('Invalid characters in search text')
      return ''
    }

    setInputError(null)
    return cleanText
  }
  // Debounced search with validation
  const debouncedSetSearchText = useCallback(
    debounce((text) => {
      const safeText = sanitizeAndValidateInput(text)
      if (safeText !== '') {
        setSearchText(safeText)
      }
    }, 500),
    [setSearchText] // ✅ fixed dependency
  )

  useEffect(() => {
    debouncedSetSearchText(inputText)
  }, [inputText, debouncedSetSearchText])

  const handleInputChange = (e) => {
    const rawValue = e.target.value
    setInputText(rawValue) // show exactly what the user types
    sanitizeAndValidateInput(rawValue) // sets error state if needed
  }

  const handleClearSearch = () => {
    setInputText('')
    setSearchText('')
    setDataSource(null)
    setInputError(null)
  }

  return (
    <div className={`search-bar ${searchP}`}>
      <InputField
        inputText={inputText}
        onChange={handleInputChange}
        label={label}
        placeholder={placeholder}
        type="text"
        prefix={
          searchText ? <CloseIcon onClick={handleClearSearch} /> : <Search />
        }
        onInputFieldChange={onSearchFieldChange}
        error={inputError}
        className="search-bar__input"
      />
      {inputError && <div className="search-bar__error">{inputError}</div>}
    </div>
  )
}

export default SearchBar
